import { useState } from 'react';
import { Mail, Phone, Globe, Instagram, Facebook, Twitter, Heart } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { LINKS } from '../config/links';

const LEGAL_TEXTS = {
  aviso: {
    title: 'Aviso legal',
    content:
      'Este portal es propiedad del Ayuntamiento de Los Alcázares y es gestionado por Valoriza Servicios Medioambientales en el marco del contrato de recogida de residuos, limpieza viaria, playas y Ecoparque del municipio. El uso de este portal está sujeto a las condiciones legales aplicables en materia de servicios de la sociedad de la información.',
  },
  privacidad: {
    title: 'Política de privacidad',
    content:
      'El Ayuntamiento de Los Alcázares, como responsable del tratamiento, garantiza el cumplimiento del Reglamento General de Protección de Datos (RGPD/UE 2016/679) y la Ley Orgánica de Protección de Datos y Garantía de los Derechos Digitales (LOPDGDD 3/2018). Los datos personales recabados serán tratados únicamente para los fines indicados y no serán cedidos a terceros sin consentimiento previo.',
  },
  cookies: {
    title: 'Política de cookies',
    content:
      'Este portal utiliza cookies propias y de terceros para mejorar la experiencia de usuario. Puedes aceptar o rechazar las cookies no esenciales en cualquier momento a través del panel de configuración. Las cookies necesarias no pueden desactivarse ya que son imprescindibles para el funcionamiento del portal.',
  },
  accesibilidad: {
    title: 'Accesibilidad',
    content:
      'Este portal aspira al cumplimiento del nivel AA de las Directrices de Accesibilidad para el Contenido Web (WCAG 2.1) y del Real Decreto 1112/2018 sobre accesibilidad de los sitios web y aplicaciones para dispositivos móviles del sector público. Si encuentras alguna barrera de accesibilidad, contáctanos en servicios@losalcazares.es.',
  },
};

export default function Footer() {
  const [legalOpen, setLegalOpen] = useState(null);

  const openLegal = (key) => setLegalOpen(key);
  const closeLegal = () => setLegalOpen(null);
  const activeLegal = legalOpen ? LEGAL_TEXTS[legalOpen] : null;

  return (
    <footer className="bg-[#0A2540] text-white" role="contentinfo">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="font-display text-2xl text-white mb-2">
              Cuidemos Los Alcázares
              <sup className="text-[0.55em] align-super">®</sup>
            </div>
            <p className="text-[#7DB9E8] text-sm mb-6">Compromiso de todos.</p>
            {/* Co-branding logos — footer oscuro */}
            <div className="flex items-center gap-3 mb-6">
              {/* Ayuntamiento: logo negro → invertido a blanco con CSS filter */}
              <div
                className="h-9 px-2 flex items-center justify-center overflow-hidden"
                title="Ayuntamiento de Los Alcázares"
              >
                <img
                  src="/ayuntamiento-logo.png"
                  alt="Ayuntamiento de Los Alcázares"
                  className="h-7 w-auto object-contain"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              </div>
              {/* Valoriza: SVG negativo (blanco+verde) funciona directo sobre fondo oscuro */}
              <div
                className="h-9 px-3 flex items-center justify-center overflow-hidden"
                title="Valoriza Servicios Medioambientales"
              >
                <img
                  src="/valoriza-logo.svg"
                  alt="Valoriza Servicios Medioambientales"
                  className="h-6 w-auto object-contain"
                />
              </div>
            </div>
            <p className="text-xs text-[#6F8AA8] leading-relaxed max-w-sm">
              Servicio municipal prestado por Valoriza Servicios Medioambientales
              en régimen de contrato con el Excmo. Ayuntamiento de Los Alcázares.
            </p>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-xs font-semibold tracking-widest uppercase text-[#7DB9E8] mb-4">
              Legal
            </h3>
            <ul className="space-y-3">
              {[
                { key: 'aviso', label: 'Aviso legal' },
                { key: 'privacidad', label: 'Privacidad (RGPD)' },
                { key: 'cookies', label: 'Política de cookies' },
                { key: 'accesibilidad', label: 'Accesibilidad WCAG 2.1' },
              ].map(({ key, label }) => (
                <li key={key}>
                  <button
                    onClick={() => openLegal(key)}
                    className="text-sm text-[#6F8AA8] hover:text-white transition-colors text-left"
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-xs font-semibold tracking-widest uppercase text-[#7DB9E8] mb-4">
              Contacto
            </h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="mailto:losalcazareslimpio@valorizasm.com"
                  className="flex items-center gap-2 text-sm text-[#6F8AA8] hover:text-white transition-colors"
                >
                  <Mail size={14} aria-hidden="true" />
                  losalcazareslimpio@valorizasm.com
                </a>
              </li>
              {LINKS.EMAIL_SERVICIO && (
                <li>
                  <a
                    href={`mailto:${LINKS.EMAIL_SERVICIO}`}
                    className="flex items-center gap-2 text-sm text-[#6F8AA8] hover:text-white transition-colors"
                  >
                    <Mail size={14} aria-hidden="true" />
                    {LINKS.EMAIL_SERVICIO}
                  </a>
                </li>
              )}
              <li>
                <a
                  href="https://www.valorizasm.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-[#6F8AA8] hover:text-white transition-colors"
                >
                  <Globe size={14} aria-hidden="true" />
                  www.valorizasm.com
                </a>
              </li>
            </ul>

            {/* Social */}
            <div className="flex gap-3 mt-6">
              {LINKS.INSTAGRAM && (
                <a href={LINKS.INSTAGRAM} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <Instagram size={18} className="text-[#6F8AA8] hover:text-white transition-colors" aria-hidden="true" />
                </a>
              )}
              {LINKS.FACEBOOK && (
                <a href={LINKS.FACEBOOK} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <Facebook size={18} className="text-[#6F8AA8] hover:text-white transition-colors" aria-hidden="true" />
                </a>
              )}
              {LINKS.X && (
                <a href={LINKS.X} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
                  <Twitter size={18} className="text-[#6F8AA8] hover:text-white transition-colors" aria-hidden="true" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <Separator className="bg-white/10" />

      {/* Bottom bar */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-[#6F8AA8]">
          © {new Date().getFullYear()} Ayuntamiento de Los Alcázares · Valoriza Servicios Medioambientales
        </p>
        <p className="text-xs text-[#6F8AA8] flex items-center gap-1">
          Hecho con <Heart size={11} className="text-[#7DB9E8]" aria-hidden /> en Los Alcázares, Murcia
        </p>
      </div>

      {/* Legal Dialog */}
      <Dialog open={!!legalOpen} onOpenChange={(v) => !v && closeLegal()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0A2540]">
              {activeLegal?.title}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6F8AA8] leading-relaxed mt-4">
            {activeLegal?.content}
          </p>
          <div className="mt-6 flex justify-end">
            <Button
              onClick={closeLegal}
              className="rounded-full bg-[#0A2540] text-white hover:bg-[#1E5AA8]"
            >
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
}
